import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {PhotoData} from '@employee-pages/badge-request-page/employee-badge-requests';
import {CommonConfig} from '@employee-configs/common.config';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {VISIT_REASON} from '@employee-pages/badge-request-page/employee-member-firm-visitor/employee-member-firm.const';
import * as EmployeeModels from '@employee-models/index';
import {REGEX_EMAIL_FORMAT, REGEX_MOBILE_FORMAT} from '@employee-models/common.enums';
import {AutomaticCroppingService, IAutoCropResultData} from '@ui-app/services/automatic-cropping.service';
import {BaseComponent} from '@ui-app/components/base.component';
import {EmployeeBusinessService} from '../../../../core/services/business.service';
import {ImageService} from '@ui-app/services/image.service';
import {IContractorRequest, IGuestForm, IGuestRequest} from '@employee-pages/badge-request-page/usi-employee-badge-forms';
import {IAllCropperData, IFileInfo} from '@shared-models/common.models';
import {of, Subject} from 'rxjs';
import {catchError, filter, switchMap} from 'rxjs/operators';
import {EReturnImageResizeType} from '@shared-models/common.enums';
import {ISearchEmployeeApprovers} from '@employee-models/common.models';
import {IBadgeRequestPhotoInfo} from '@employee-models/data.models';
import {VISA_TYPE} from '@employee-pages/shared/components/fields-for-forms/visa-type/visa-type.model';
import { BadgeRequestCategoryEnum } from '@employee-models/data.enums';
import { UserRoleEnum } from '@employee-models/data.enums';

export enum GuestFormType {
    EDIT = 0,
    ADD = 1,
}

@Component({
    template: '',
})
export abstract class GuestManagementComponent extends BaseComponent implements OnInit {
    @Output() guestAddedOrEdited = new EventEmitter<any>();
    @Output() cancel = new EventEmitter<boolean>();
    @Input() isBadgeExtension: boolean = false; 
    isCleRequest: boolean = false;
    title: string;
    buttonSubmitTitle: string;
    guestFormType: GuestFormType;
    readonly GuestFormType = GuestFormType;
    user: any;
    DisplayDate= 90;
    protected readonly BadgeRequestCategoryEnum = BadgeRequestCategoryEnum;

    readonly uploadConfig = {
        ...CommonConfig.fileUploadRestrictions.photo,
        areaTitle: 'Drag & Drop photo here or',
        customRestrictions: 'Provide a photo that meets all mentioned requirements',
        errors: {
            filesCount: 'Only one file can be uploaded',
            faceRecognition: `The photo isn't individual or face isn't clear on the photo. Please, upload another photo`,
        },
    };

    readonly uploadAttachmentConfig = {
        ...CommonConfig.fileUploadRestrictions.attachment,
        areaTitle: 'Drag & Drop file here or',
        customRestrictions: 'You can add file with max size 5 MB <br> Supported formats: PDF, XLS, DOC, PPT, JPEG, JPG, PNG, ZIP',
        errors: {
            filesCount: 'Only one file can be uploaded',
            faceRecognition: `The photo isn't individual or face isn't clear on the photo. Please, upload another photo`,
        },
    };

    defaultImageUrl = '../../assets/img/badge-requests/no-photo.svg';
    addGuestForm: FormGroup<IGuestForm>;

    visaTypes = VISA_TYPE;
    selectedVisaType: string;
    userPhotoData = null;
    attachmentData = null;
    photoData: PhotoData;
    userPhotoUrl: string = null;
    isExtensionRequest = false;

    isImageRecognitionEnabled = true;
    automaticallyCropTheImage$: Subject<void> = new Subject<void>();
    croppedPhotoBase64: string = null;
    croppedPhotoSmallBase64: string = null;
    cropperData: IAllCropperData;
    imageResize$: Subject<void> = new Subject();

    selectedApprovers: ISearchEmployeeApprovers[] = [];

    protected readonly reasons = VISIT_REASON;

    get buttonTitle(): string {
        return this.userPhotoUrl ? 'Change photo' : 'Upload photo';
    }
    
    // get isAddAvailable(): boolean {
    //     const isPhotoUploaded = !!this.userPhotoUrl;
    //     const isFormValid = this.addGuestForm.valid;
    //     const isFormDirty = this.addGuestForm.dirty;
    
    //     if (this.guestFormType === GuestFormType.ADD) {
    //         return !(isFormValid && isFormDirty && isPhotoUploaded);
    //     } else {
    //         return !(isFormDirty && isPhotoUploaded);
    //     }
    // }
    get isAddAvailable(): boolean {
    const isFormValid = this.addGuestForm.valid;
    const isFormDirty = this.addGuestForm.dirty;
    if (this.guestFormType === GuestFormType.ADD) {
        if (this.isBadgeExtension) {
            // For badge extension, photo is not required
            return !(isFormValid && isFormDirty);
        }
        // For initial, photo is required
        const isPhotoUploaded = !!this.userPhotoUrl;
        return !(isFormValid && isFormDirty && (isPhotoUploaded || this.isCleRequest));
    } else {
        // EDIT mode: Button disabled initially, enabled only when form is dirty and valid
        if (this.isBadgeExtension) {
            return !(isFormValid && isFormDirty);
        }
        const isPhotoUploaded = !!this.userPhotoUrl;
        return !(isFormValid && isFormDirty && (isPhotoUploaded || this.isCleRequest));
    }
}

    get numOfDays(): number {
        const start = new Date(this.addGuestForm.value.visitStart);
        const end = new Date(this.addGuestForm.value.visitEnd);
        if (start && end && (start <= end)) {
            const timeDiff = end.getTime() - start.getTime();
            const dayDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
            return dayDiff;
        } else {
            return 0;
        }
    }

    get minDate(): string {
        const start = this.addGuestForm.value.visitStart;
        return start ? start : null;
    }

    get isSeniorManager(): boolean {
        return (this.user?.userRoleIds?.length ? this.user.userRoleIds : [this.user?.userRoleId]).includes(81);
    }

    get isThirdPartyContractorApprover(): boolean {
        return (this.user?.userRoleIds?.length ? this.user.userRoleIds : [this.user?.userRoleId]).includes(12);
    }

    constructor(
        protected fb: FormBuilder,
        protected automaticCroppingService: AutomaticCroppingService,
        protected employeeBusinessService: EmployeeBusinessService,
        protected imageService: ImageService,
    ) {
        super();
        this.employeeBusinessService.userProfileService.userData$.subscribe(data => this.user = data);
    }

    ngOnInit(): void {
        this.createForm();
        this.setupCropImage();
        this.setupImageResize();
    }

    createForm(): void {
        // @ts-ignore
        this.addGuestForm = this.fb.group({
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            phoneNumber: ['', [Validators.required, Validators.pattern(REGEX_MOBILE_FORMAT)]],
            emailAddress: ['', [Validators.required, Validators.pattern(REGEX_EMAIL_FORMAT)]],
            company: ['', Validators.required],
            visaTypeId: [0, Validators.required],
            entityOrFssId: [0, Validators.required],
            notification: [''],
            photoInfo: [null],
            attachmentGuid: [null],
            visitStart: ['', [Validators.required]],
            visitEnd: ['', Validators.required],
            approvers: [null, [Validators.required]],
            approversFull: [[]],
            //accessRequiredIds: [null, Validators.required],
            accessRequiredIds: [[], Validators.required],
            visitReasonId: [null, [Validators.required]]
        });
        if (this.isSeniorManager) {
                  this.addGuestForm.controls.approvers.clearValidators();
                  this.addGuestForm.updateValueAndValidity();
            }
    }

    setApprovers(approverIds: number[]): void {
        this.addGuestForm.patchValue({
            approvers: approverIds
        });
    }

    setFullApprovers(approvers: ISearchEmployeeApprovers[]): void {
        this.addGuestForm.patchValue({
            approversFull: approvers
        });
    }

    cancelGuest(): void {
        this.resetForm();
        this.cancel.emit(true);
    }

    resetForm(): void {
        this.addGuestForm.reset();
        this.photoData = null;
        this.userPhotoUrl = null;
    }

    onVisaTypeChange(value: string): void {
        const visa = this.visaTypes.find((item) => item.name === value);
        this.addGuestForm.get('visaTypeId').setValue(visa.id);
    }

    validateNotBeforeToday(control: any): { [key: string]: boolean } | null {
        if (!control?.value) {
            return null;
        }

        const selectedDate = new Date(control.value);
        selectedDate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return selectedDate < today ? { minDate: true } : null;
    }

    onPhotoUploaded(event: PhotoData): void {
        if (event.length > 0) {
            this.photoData = event;
            const file = this.getFileFromPhotoData(this.photoData[0]);
            if (file) {
                this.userPhotoData = { uId: file?.uId };
                this.userPhotoUrl = file?.url;
                this.addGuestForm.patchValue({
                    photoInfo: { file, croppInfo: null },
                });
                this.automaticallyCropTheImage$.next();
    
                // Mark the form as dirty only if in edit mode and the form is valid
                if (this.guestFormType === GuestFormType.EDIT && this.addGuestForm.valid) {
                    this.addGuestForm.markAsDirty();
                }
            }
        }
    }

    getFileFromPhotoData(photoData: PhotoData): IBadgeRequestPhotoInfo {
        const { uId, contentType, length, url, name } = photoData;
        return {
            uId,
            name,
            contentType,
            length,
            url,
            documentType: EmployeeModels.DE.DocumentTypeEnum.FrontFace,
        };
    }

    // submit(): void {
    //     const formValue = {
    //         ...this.addGuestForm.value,
    //         visitStart: this.addGuestForm.value.visitStart.toString(),
    //         visitEnd: this.addGuestForm.value.visitEnd.toString(),
    //         approvers: this.user.userRoleId==81? [this.user.userId] : this.addGuestForm.value.approvers
    //         //UserRoleId==81 is Senior Manager Role as returned from the user API in frontend
    //     } as any;
    //     this.guestFormType === GuestFormType.ADD ? this.addGuest(formValue) : this.editGuest(formValue);
    // }
    submit(): void {
        const formValue = {
            ...this.addGuestForm.value,
            visitStart: this.addGuestForm.value.visitStart.toString(),
            visitEnd: this.addGuestForm.value.visitEnd.toString(),
            approvers: this.isSeniorManager ? [this.user.userId] : this.addGuestForm.value.approvers,
            isBadgeExtension: this.isBadgeExtension, // <-- Add this to payload
            photoInfo: this.addGuestForm.value.photoInfo || {}
        } as any;
        
        if (this.isBadgeExtension) {
            formValue.photoInfo = {};
        }

        this.guestFormType === GuestFormType.ADD ? this.addGuest(formValue) : this.editGuest(formValue);
    }

    // FOR OVERRIDE PURPOSES IN ADD / EDIT COMPONENTS
    addGuest(formValue: IGuestRequest | IContractorRequest): void {
    }

    editGuest(formValue: IGuestRequest): void {
    }

    dateChanged(): void {
        const start = Date.parse(this.addGuestForm.value.visitStart);
        const end = Date.parse(this.addGuestForm.value.visitEnd);
        if (start > end) {
            this.addGuestForm.patchValue({
                visitEnd: null
            });
        }
    }

    onAttachmentUpload(event: IFileInfo[]): void {
        if (event.length > 0) {
            const data = event[0] as PhotoData;
            const file = this.getFileFromPhotoData(data);
            this.attachmentData = file;
            this.addGuestForm.patchValue({
                attachmentGuid: file.uId,
            });
        }
    }

    clearAttachment(): void {
        this.attachmentData = null;
        this.addGuestForm.patchValue({
            attachmentGuid: null,
        });
    }

    resetPhoto(): void {
        this.userPhotoData = null;
        this.isImageRecognitionEnabled = true;
        this.croppedPhotoBase64 = null;
        this.croppedPhotoSmallBase64 = null;
        this.photoData = null;
        this.userPhotoUrl = null;
    }

    setupCropImage(): void {
        this.subs = this.automaticallyCropTheImage$
            .pipe(
                filter(() => this.isImageRecognitionEnabled),
                switchMap(() => {
                        return this.automaticCroppingService
                            .automaticallyCropTheImage(this.userPhotoData.uId, 250, 375, true)
                            .pipe(
                                catchError((() => {
                                    this.resetPhoto();
                                    return of(null);
                                })),
                                filter(x => !!x)
                            );
                    }
                )).subscribe((croppedData: IAutoCropResultData) => {
                this.croppedPhotoBase64 = croppedData.croppedImageBase64;
                this.cropperData = croppedData.cropperData;
                this.addGuestForm.patchValue({
                    photoInfo: {
                        ...this.addGuestForm.value.photoInfo,
                        croppInfo: JSON.stringify(this.cropperData),
                    }
                });
                this.isImageRecognitionEnabled = croppedData.isImageRecognitionEnabled;
                this.imageResize$.next();
            });
    }

    setupImageResize(): void {
        this.subs = this.imageResize$
            .pipe(switchMap(() => {
                return this.imageService.changeImageSize(
                    this.imageService.fromDataUrlToFile(this.croppedPhotoBase64, 'tmpFilename'),
                    this.imageService.imageDefaultType,
                    600,
                    EReturnImageResizeType.dataUrl,
                    'tmpFilename'
                );
            })).subscribe((smallBase64: string) => {
                this.croppedPhotoSmallBase64 = smallBase64;
            });
    }

}
